<script>
import { ActionTypes, StatusTypes, useClientStore } from "@/store/client";
import { mapActions, mapState } from "pinia";
import { usePageMonCompte } from "./mixin";
import ProduitsHabituelsOverview from "@/components/organisms/mon-compte/produits-habituels-overview/default";
import EspaceFideliteOverview from "@/components/organisms/mon-compte/fidelite-espace-overview/default";
import ListesDeCourseOverview from "@/components/organisms/mon-compte/listes-de-courses-overview/default";
import CommandeOverview from "@/components/organisms/mon-compte/commande-overview/default";
import MagasinOverview from "@/components/organisms/mon-compte/magasin-overview";
import AdresseLadOverview from "@/components/organisms/mon-compte/adresse-lad-overview";
import GridRow from "@/components/molecules/grid/grid-row";
import GridCol from "@/components/molecules/grid/grid-col";
import List from "@/components/molecules/list";
import ListItem from "@/components/molecules/list/list-item";
import Button from "@/components/atoms/button";
import Card from "@/components/molecules/card";
import Popin from "@/components/molecules/popin";
import MagasinSelector from "@/containers/magasin-selector";
import AbonnementOverview from "@/components/organisms/mon-compte/abonnement-overview/default";
import { isMagasinBascule } from "@/utils/magasin";

import apiDriveFidelite from "@/services/http/api-drive/client/fidelite";
import apiDriveSmartliste from "@/services/http/api-drive/client/smartliste";
import apiDriveCommande from "@/services/http/api-drive/client/commande";
import apiDriveAdresse from "@/services/http/api-drive/client/adresseLad";

export default {
  name: "MonCompteDashboard",
  components: {
    GridRow,
    GridCol,
    ProduitsHabituelsOverview,
    EspaceFideliteOverview,
    ListesDeCourseOverview,
    CommandeOverview,
    List,
    ListItem,
    Button,
    MagasinOverview,
    AdresseLadOverview,
    Card,
    Popin,
    MagasinSelector,
    AbonnementOverview
  },
  setup() {
    const magasinSelectorPopin = ref(null);
    const {
      client,
      isPro,
      abonnements,
      magasinId,
      isEligibleAliasPaiement,
      listesDeCourse,
      listesDeCourseCount,
      listesDeCourseLoading,
      magasin,
      magasinLoading,
      state,
      error,
      getListesDeCourse,
      getMagasin,
      changeMagasinRattachement,
      isFidCarrefour,
    } = usePageMonCompte(magasinSelectorPopin);

    return {
      client,
      isPro,
      abonnements,
      magasinId,
      isEligibleAliasPaiement,
      listesDeCourse,
      listesDeCourseCount,
      listesDeCourseLoading,
      magasin,
      magasinLoading,
      state,
      error,
      getListesDeCourse,
      getMagasin,
      changeMagasinRattachement,
      magasinSelectorPopin,
      isFidCarrefour,
    };
  },
  data() {
    return {
      produitsHabituels: [],
      produitsHabituelsLoading: false,
      fidelite: [],
      fideliteLoading: false,
      urlEspaceFidelite: "",
      commandes: [],
      commandesLoading: false,
      adresse: null,
      adresseLoading: false
    };
  },
  computed: {
    ...mapState(useClientStore, ["status", "popinAjoutCarte"])
  },

  watch: {
    status: {
      async handler(value) {
        if (
          value.action === ActionTypes.UPDATE &&
          value.type === StatusTypes.CLIENT_SUCCESS
        ) {
          this.fideliteLoading = true;
          try {
            await this.getFidelite();
          } catch (e) {}
        }
      },
      immediate: true
    }
  },
  async mounted() {
    this.listesDeCourseLoading = true;
    this.fideliteLoading = true;
    this.produitsHabituelsLoading = true;
    this.commandesLoading = true;

    try {
      await this.getListesDeCourse();
    } catch (e) {}

    try {
      const res = await this.$apiDrive
        .use(apiDriveSmartliste)
        .smartliste(this.magasinId);
      this.produitsHabituels = res.data.attributes.produits.data.slice(0, 2);
    } finally {
      this.produitsHabituelsLoading = false;
    }

    try {
      await this.getFidelite();
    } catch (e) {}

    try {
      const res = await this.$apiDrive.use(apiDriveCommande).commandes();
      this.commandes = res.data;
      if (res.meta.has_archive && this.commandes.length < 2) {
        const resArchive = await this.$apiDrive
          .use(apiDriveCommande)
          .commandes({ archive: 1 });
        this.commandes = this.commandes.concat(resArchive.data);
      }
      this.commandes = this.commandes.slice(0, 2);
    } finally {
      this.commandesLoading = false;
    }

    try {
      await this.getMagasin();
    } catch (e) {}

    if (this.isPro) {
      return;
    }

    this.adresseLoading = true;
    try {
      const adresses = await this.$apiDrive
        .use(apiDriveAdresse)
        .adressesLad({ defaut: 1 });
      if (adresses && adresses.data && adresses.data.length > 0) {
        const adresseId = adresses.data[0].id;
        if (adresseId) {
          const adresse = await this.$apiDrive
            .use(apiDriveAdresse)
            .adresseLad(adresseId);
          this.adresse = adresse.data;
        }
      }
    } finally {
      this.adresseLoading = false;
    }
  },
  methods: {
    ...mapActions(useClientStore, {
      setPopinAjoutCarteVisible: "setPopinAjoutCarteVisible"
    }),
    setPopinAjoutCarte() {
      this.setPopinAjoutCarteVisible(true);
    },
    async getFidelite() {
      if (this.isPro) {
        this.fideliteLoading = false;
        return Promise.resolve();
      }
      try {
        const response = await this.$apiDrive.use(apiDriveFidelite).fidelites();
        this.fidelite = response.data;
        this.urlEspaceFidelite = response.meta
          ? response.meta.url_espace_client
          : "";
      } finally {
        this.fideliteLoading = false;
      }
    }
  }
};
</script>

<template>
  <div>
    <h1 class="u-color-primary u-font-secondary u-font-24 u-font-700 u-mb-md">
      Bonjour {{ client.attributes.prenom }}
    </h1>
    <div class="c-mon-compte-dashboard__liste-de-course">
      <ListesDeCourseOverview
        :listes="listesDeCourse"
        :count="listesDeCourseCount"
        :is-loading="listesDeCourseLoading"
        class="u-full-height"
      />
      <ProduitsHabituelsOverview
        :produits="produitsHabituels"
        :is-loading="produitsHabituelsLoading"
        class="u-full-height"
      />
      <EspaceFideliteOverview
        v-if="!isPro"
        :cartes="fidelite"
        :is-loading="fideliteLoading"
        :is-fid-carrefour="isFidCarrefour"
        :url-espace-fidelite="urlEspaceFidelite"
        class="u-full-height"
        @addCarte="setPopinAjoutCarte"
      />
    </div>
    <h2 class="u-mb-sm u-mt-xs u-font-19 u-font-secondary u-font-700">
      Mes dernières commandes
      <Button
        link
        class="u-ml-md u-font-400"
        to="/mon-compte/mes-commandes"
      >
        Voir toutes mes commandes
      </Button>
    </h2>
    <template v-if="commandes.length > 0">
      <List>
        <ListItem
          v-for="commande in commandes"
          :key="commande.id"
          class="u-mb-md"
        >
          <CommandeOverview :commande="commande" />
        </ListItem>
      </List>
    </template>
    <Card
      v-if="!commandesLoading && commandes.length == 0"
      radius="md"
      elevation="sm"
      class="c-mon-compte-dashboard__pas-commande"
    >
      <div class="u-flex u-flex-align-center u-flex-justify-between u-p-md">
        <p>
          Vous n'avez passé aucune commande pour le moment
        </p>
        <Button
          variant="secondary"
          radius="full"
          size="md"
          to="/"
        >
          Faire mes courses
        </Button>
      </div>
    </Card>
    <template v-if="!isPro && abonnements && abonnements.length !== 0">
      <h2 class="u-mb-sm u-mt-xs u-font-19 u-font-secondary u-font-700">
        Mes abonnements
        <Button
          link
          to="/mon-compte/abonnements"
          class="u-ml-md u-font-400"
        >
          Voir tous mes abonnements
        </Button>
      </h2>
      <List>
        <ListItem
          v-for="(abonnement,i) in abonnements"
          :key="i"
          class="u-mb-md"
        >
          <AbonnementOverview :abonnement="abonnement" />
        </ListItem>
      </List>
    </template>

    <h2 v-if="adresse || (magasin && !isMagasinBascule(magasin))" class="u-mb-sm u-mt-lg u-font-19 u-font-secondary u-font-700">
      Mes habitudes
    </h2>
    <GridRow>
      <GridCol
        v-if="magasin && !isMagasinBascule(magasin)"
        col="4"
        lg="6"
        md="12"
        class="u-mb-md"
      >
        <Card
          v-if="magasin"
          class="u-p-md u-full-height"
          radius="lg"
          elevation="sm"
        >
          <MagasinOverview
            :magasin="magasin"
            :can-change="!isPro"
            @change="$refs.magasinSelectorPopin.show()"
          />
        </Card>
      </GridCol>
      <GridCol
        col="4"
        lg="6"
        md="12"
        class="u-mb-md"
      >
        <Card
          v-if="adresse"
          class="u-p-md u-full-height"
          radius="lg"
          elevation="sm"
        >
          <AdresseLadOverview
            :adresse="adresse"
            :is-loading="adresseLoading"
          />
        </Card>
      </GridCol>
    </GridRow>
    <Popin
      ref="magasinSelectorPopin"
      aria-label="Sélectionner un magasin"
    >
      <MagasinSelector
        :state="state"
        :error="error"
        @selectMagasin="changeMagasinRattachement"
      />
    </Popin>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/mon-compte-dashboard.scss";
</style>
